import React, { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Loader2, RefreshCw, PlayCircle, Wrench } from 'lucide-react';

export const AdminDiscoveryControl = () => {
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [maxResults, setMaxResults] = useState(5);
  const { toast } = useToast();

  const fixStuckJobs = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-discovery-control', {
        body: { action: 'fix_stuck_jobs' }
      });

      if (error) throw error;

      toast({
        title: "Jobs Fixed",
        description: `Fixed ${data.jobs?.length || 0} stuck discovery jobs`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const triggerDiscovery = async () => {
    if (!searchQuery.trim()) {
      toast({
        title: "Search Required",
        description: "Please enter a search query",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-discovery-control', {
        body: { 
          action: 'trigger_discovery',
          searchQuery,
          maxResults 
        }
      });

      if (error) throw error;

      toast({
        title: "Discovery Started",
        description: `Searching for: ${searchQuery}`,
      });
      
      setSearchQuery('');
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="border-border/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wrench className="h-5 w-5" />
          Discovery Control Panel
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Fix Stuck Jobs */}
        <div className="space-y-2">
          <h3 className="text-sm font-medium">System Maintenance</h3>
          <Button 
            onClick={fixStuckJobs} 
            disabled={loading}
            variant="outline"
            className="w-full"
          >
            {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
            Fix Stuck Discovery Jobs
          </Button>
          <p className="text-xs text-muted-foreground">
            Cleans up discovery jobs stuck in "running" state for &gt;10 minutes
          </p>
        </div>

        {/* Manual Discovery */}
        <div className="space-y-3 pt-4 border-t">
          <h3 className="text-sm font-medium">Manual Discovery</h3>
          <div className="space-y-2">
            <Input
              placeholder="Search query (e.g., 'healthcare AI chatbot')"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={loading}
            />
            <Input
              type="number"
              placeholder="Max results (default: 5)"
              value={maxResults}
              onChange={(e) => setMaxResults(parseInt(e.target.value) || 5)}
              min={1}
              max={20}
              disabled={loading}
            />
            <Button 
              onClick={triggerDiscovery} 
              disabled={loading || !searchQuery.trim()}
              className="w-full"
            >
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlayCircle className="mr-2 h-4 w-4" />}
              Start Discovery
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Manually trigger chatbot discovery with custom search query
          </p>
        </div>

        {/* Info */}
        <div className="pt-4 border-t space-y-2">
          <h3 className="text-sm font-medium">Discovery Status</h3>
          <div className="text-xs text-muted-foreground space-y-1">
            <p>✅ Enrichment validation: Bots without valid enrichment are skipped</p>
            <p>✅ Safety checks: URLs are validated before processing</p>
            <p>✅ Duplicate prevention: Existing bots won't be re-discovered</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};